import React from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import DashboardShell from "@/components/dashboard/DashboardShell";
import DriverDocuments from "@/components/driver/DriverDocuments";
import DriverVerifyBadge from "@/components/driver/DriverVerifyBadge";
import DriverPayoutsPanel from "@/components/driver/DriverPayoutsPanel";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import {
  ShieldAlert, Wallet, BadgeCheck, MapPin, Calendar, Tag, Loader2,
  AlertCircle, X, Send, CheckCircle2, Clock, Trash2, FileText,
  Banknote, ExternalLink, ArrowRight, Truck, PlayCircle, Flag, Star, Camera,
} from "lucide-react";
import RatingModal from "@/components/RatingModal";
import JobTimerPanel from "@/components/JobTimerPanel";
import DriverMembershipSection from "@/components/driver/DriverMembershipSection";
import ClockInOutModal from "@/components/driver/ClockInOutModal";
import DriverReferralPanel from "@/components/driver/DriverReferralPanel";
import DriverWhatsAppToggle from "@/components/driver/DriverWhatsAppToggle";
import DriverShiftPanel from "@/components/driver/DriverShiftPanel";
import DisputesSection from "@/components/disputes/DisputesSection";

const QUOTE_STATUS_META = {
  pending: { label: "Pending", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  accepted: { label: "Accepted", cls: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40" },
  rejected: { label: "Rejected", cls: "bg-[#B91C1C]/10 text-[#B91C1C] border-[#FCA5A5]" },
  withdrawn: { label: "Withdrawn", cls: "bg-[#475569]/10 text-[#475569] border-[#475569]/30" },
};

function gbp(p) {
  if (p == null) return "—";
  return `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function QuoteStatusPill({ status }) {
  const m = QUOTE_STATUS_META[status] || QUOTE_STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function DriverDashboard() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = React.useState(null);

  const loadProfile = React.useCallback(async () => {
    try {
      const { data } = await apiClient.get("/driver/profile");
      setProfile(data);
      refresh();
    } catch {
      /* ignored */
    }
  }, [refresh]);

  React.useEffect(() => { loadProfile(); }, [loadProfile]);

  const isVerified = !!profile?.user?.verified || !!user?.verified;
  const isSuspended = !!profile?.user?.suspended_for_expired_docs || !!user?.suspended_for_expired_docs;
  const suspendedDocs = profile?.user?.suspended_expired_docs || user?.suspended_expired_docs || [];
  const approved = profile?.required_approved ?? 0;
  const total = profile?.required_total ?? 5;
  const pct = total > 0 ? Math.round((approved / total) * 100) : 0;

  // v23.22 — Managed drivers (internal Raanah team) get a stripped dashboard:
  // just the ID card + shift clocking tool. They don't need KYC, docs, jobs, quotes, referrals or Stripe Connect.
  const driverType = profile?.user?.driver_type || user?.driver_type || "self_employed";
  const isManaged = driverType === "managed";

  if (isManaged) {
    return (
      <DashboardShell eyebrow="Raanah team" title={`Hi ${user?.name?.split(" ")[0] || "there"}.`}>
        <div data-testid="managed-driver-banner" className="bg-[#1E4D55]/5 border border-[#1E4D55]/30 p-6 mb-10">
          <div className="flex items-start gap-4">
            <BadgeCheck className="text-[#1E4D55] shrink-0" size={28} />
            <div>
              <p className="font-display text-xl font-bold tracking-tight text-[#0A0A0A]">
                Managed driver · Raanah team member.
              </p>
              <p className="mt-2 text-[#475569] leading-relaxed">
                You&apos;re set up as an internal driver, so you don&apos;t need to upload your own insurance or vehicle documents. Use the tools below to clock in and to show your ID at the door. We&apos;ll add more tools here as we grow.
              </p>
            </div>
          </div>
        </div>

        {/* v23.21 — Shift timesheet (rota) */}
        <DriverShiftPanel />

        {/* v23.2 — Raanah ID card (QR badge customers scan at the door) */}
        <DriverVerifyBadge />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell eyebrow="Driver" title={`Hi ${user?.name?.split(" ")[0] || "there"}.`}>
      {/* v23.9 — Doc-expiry suspension banner (overrides the verification banner) */}
      {isSuspended && (
        <div data-testid="driver-suspended-banner" className="bg-[#B91C1C]/10 border border-[#B91C1C]/40 p-6 mb-6">
          <div className="flex items-start gap-4">
            <ShieldAlert className="text-[#B91C1C] shrink-0" size={28} />
            <div className="flex-1">
              <p className="font-display text-xl font-bold tracking-tight text-[#B91C1C]">
                Your account is paused — expired document(s).
              </p>
              <p className="mt-2 text-[#7F1D1D] leading-relaxed">
                {suspendedDocs.length > 0
                  ? <>The following document{suspendedDocs.length === 1 ? "" : "s"} expired: <strong>{suspendedDocs.join(", ")}</strong>.</>
                  : "One of your required documents expired."}
                {" "}You can&apos;t accept new jobs until you re-upload below and admin approves. Active jobs already assigned to you are unaffected.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Verification banner */}
      <div
        data-testid="driver-verification-banner"
        className={`border p-6 mb-12 ${
          isVerified
            ? "bg-[#5BB247]/10 border-[#5BB247]/40"
            : "bg-[#F5A623]/10 border-[#F5A623]/40"
        }`}
      >
        <div className="flex items-start gap-4">
          {isVerified ? (
            <BadgeCheck className="text-[#5BB247] shrink-0" size={28} />
          ) : (
            <ShieldAlert className="text-[#F5A623] shrink-0" size={28} />
          )}
          <div className="flex-1">
            <p className="font-display text-xl font-bold tracking-tight text-[#0A0A0A]">
              {isVerified
                ? "You're verified. Ready for jobs."
                : "Verification pending."}
            </p>
            <p className="mt-2 text-[#475569] leading-relaxed">
              {isVerified
                ? "Browse open jobs below and submit quotes. Winning quotes notify you instantly."
                : "Upload your required documents below. Admin reviews each one within 24 hours."}
            </p>

            {!isVerified && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">
                  <span data-testid="driver-progress-label">
                    Required docs approved: {approved}/{total}
                  </span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1.5 bg-white border border-[#0A0A0A]/10 overflow-hidden">
                  <div
                    data-testid="driver-progress-bar"
                    className="h-full bg-[#1E4D55] transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats placeholders */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-[#0A0A0A]/10 mb-12">
        {[
          { label: "This week", value: "£0", caption: "Earned" },
          { label: "Lifetime", value: "£0", caption: "Earned" },
          { label: "Jobs", value: "0", caption: "Completed" },
        ].map((stat) => (
          <div
            key={stat.label}
            data-testid={`driver-stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="bg-white border-r border-b border-[#0A0A0A]/10 p-6"
          >
            <div className="flex items-start justify-between">
              <Wallet size={18} className="text-[#1E4D55]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#475569]">
                {stat.label}
              </span>
            </div>
            <p className="mt-6 font-display text-4xl font-black tracking-tighter text-[#0A0A0A]">
              {stat.value}
            </p>
            <p className="mt-1 text-xs text-[#475569]">{stat.caption}</p>
          </div>
        ))}
      </div>

      {/* Stripe Connect bank account section (only when verified) */}
      {isVerified && <StripeConnectSection />}

      {/* v23.21 — Shift timesheet (rota) — placed near the top so drivers can clock in easily */}
      <DriverShiftPanel />

      {/* Active escrow-paid jobs */}
      <ActiveJobsSection />

      {/* Jobs Board (only when verified) */}
      <AvailableJobsSection isVerified={isVerified} />

      {/* My Quotes */}
      <MyQuotesSection />

      <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] mb-6 mt-16">
        Documents
      </h2>
      <DriverDocuments onChange={loadProfile} />

      {/* v23.2 — Customer-scannable verify badge (only useful once verified) */}
      {isVerified && <DriverVerifyBadge />}

      {/* v23.4 — Payouts & bank management (Stripe Connect dashboard) */}
      {isVerified && <DriverPayoutsPanel />}

      {/* v23.14 — Driver-to-driver referral program */}
      <DriverReferralPanel />

      {/* v23.18 — WhatsApp opt-in toggle */}
      <DriverWhatsAppToggle />

      {/* v23.14 — Disputes */}
      <DisputesSection role="driver" />
    </DashboardShell>
  );
}

/* ---------------- Active escrow-paid jobs ---------------- */

const ACTIVE_JOB_META = {
  accepted: { label: "Awaiting customer payment", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  paid: { label: "Funds in escrow · ready to start", cls: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40" },
  in_progress: { label: "In progress", cls: "bg-[#1E4D55]/10 text-[#1E4D55] border-[#1E4D55]/40" },
  ready_for_completion: { label: "Waiting on customer", cls: "bg-[#F5A623]/10 text-[#A66800] border-[#F5A623]/40" },
  completed: { label: "Completed · paid out", cls: "bg-[#5BB247]/10 text-[#2c8642] border-[#5BB247]/40" },
};

function ActiveJobsSection() {
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [acting, setActing] = React.useState(null);
  const [rateCustomerForJob, setRateCustomerForJob] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/driver/jobs/active");
      setJobs(data.jobs || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load active jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const [clockJob, setClockJob] = React.useState(null); // {id, title, kind}

  const startJob = (job) => {
    setClockJob({ id: job.id, title: job.title, kind: "clock_in" });
  };

  const markReady = (job) => {
    setClockJob({ id: job.id, title: job.title, kind: "clock_out" });
  };

  const handleClockDone = async () => {
    const isClockOut = clockJob?.kind === "clock_out";
    const closingJobId = clockJob?.id;
    setClockJob(null);
    await load();
    if (isClockOut && closingJobId) setRateCustomerForJob(closingJobId);
  };

  // v23.11 — Driver confirms balance received offline (cash/bank)
  const confirmBalance = async (job) => {
    if (!window.confirm(`Confirm you've received the £${((job.balance_pence || 0)/100).toFixed(2)} balance from the customer? This marks the job as completed.`)) return;
    setActing(job.id);
    try {
      await apiClient.post(`/driver/jobs/${job.id}/confirm-balance`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to confirm balance");
    } finally {
      setActing(null);
    }
  };

  if (loading) return null;
  if (jobs.length === 0) return null;

  return (
    <section className="mb-16" data-testid="driver-active-jobs-section">
      <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] mb-6">
        Your active jobs
      </h2>
      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}
      <div className="space-y-3">
        {jobs.map((j) => {
          const meta = ACTIVE_JOB_META[j.status] || ACTIVE_JOB_META.accepted;
          const driverShare = (j.amount_pence || 0) - (j.platform_fee_pence || 0);
          return (
            <div key={j.id} data-testid={`driver-active-job-${j.id}`} className="bg-white border border-[#0A0A0A]/10 p-5">
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#1E4D55]">
                      {j.category_label}
                    </span>
                    <span className={`inline-flex items-center gap-1 border px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </div>
                  <p className="mt-2 font-display text-xl font-bold tracking-tight text-[#0A0A0A]">
                    {j.title}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#475569]">
                    <span className="inline-flex items-center gap-1"><MapPin size={12} /> {j.pickup_address}{j.dropoff_address ? ` → ${j.dropoff_address}` : ""}</span>
                    {j.scheduled_date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {j.scheduled_date}</span>}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#475569]">Your share</p>
                  <p className="font-display text-2xl font-black tracking-tighter text-[#1E4D55]">{gbp(driverShare)}</p>
                  <p className="text-[10px] text-[#475569]">of {gbp(j.amount_pence)} total</p>
                </div>
              </div>

              {j.status === "paid" && (
                <button
                  onClick={() => startJob(j)}
                  disabled={acting === j.id}
                  data-testid={`driver-start-job-${j.id}`}
                  className="mt-4 inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {acting === j.id ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  Clock in
                </button>
              )}

              {j.status === "in_progress" && (
                <button
                  onClick={() => markReady(j)}
                  disabled={acting === j.id}
                  data-testid={`driver-mark-ready-${j.id}`}
                  className="mt-4 inline-flex items-center gap-2 bg-[#5BB247] hover:bg-[#4A9939] text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {acting === j.id ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  Clock out
                </button>
              )}

              {j.status === "ready_for_completion" && (
                <p className="mt-4 text-xs text-[#475569] inline-flex items-center gap-1">
                  <Clock size={12} /> Waiting on customer to confirm completion and release {gbp(driverShare)} to your bank.
                </p>
              )}

              {/* v23.11 — Deposit job · balance pending offline confirmation */}
              {j.payment_mode === "deposit" && !j.balance_paid && ["in_progress", "ready_for_completion"].includes(j.status) && (
                <div data-testid={`driver-balance-pending-${j.id}`} className="mt-4 bg-[#F5A623]/5 border border-[#F5A623]/40 p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#A66800]">
                        Balance pending · {gbp(j.balance_pence)}
                      </p>
                      <p className="mt-1 text-xs text-[#475569] max-w-md">
                        Customer paid {gbp(j.deposit_pence)} deposit. If they pay the balance offline (cash / bank transfer), confirm receipt here to mark the job complete.
                      </p>
                    </div>
                    <button
                      onClick={() => confirmBalance(j)}
                      disabled={acting === j.id}
                      data-testid={`driver-confirm-balance-${j.id}`}
                      className="inline-flex items-center gap-2 bg-[#5BB247] hover:bg-[#4A9939] text-white px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
                    >
                      {acting === j.id ? <Loader2 size={14} className="animate-spin" /> : <Banknote size={14} />}
                      Confirm payment received
                    </button>
                  </div>
                </div>
              )}

              {/* v18.5 — Live timer + overtime panel (auto-priced jobs only) */}
              {j.auto_priced && j.timer && (
                <JobTimerPanel timer={j.timer} audience="driver" />
              )}
            </div>
          );
        })}
      </div>

      {rateCustomerForJob && (
        <RatingModal
          endpoint={`/driver/jobs/${rateCustomerForJob}/rate-customer`}
          title="How was your customer?"
          subtitle="Optional — your rating helps other drivers pick the right customers."
          optional
          onClose={() => setRateCustomerForJob(null)}
        />
      )}

      {clockJob && (
        <ClockInOutModal
          jobId={clockJob.id}
          jobTitle={clockJob.title}
          kind={clockJob.kind}
          onClose={() => setClockJob(null)}
          onDone={handleClockDone}
        />
      )}
    </section>
  );
}

/* ---------------- Stripe Connect (bank onboarding) ---------------- */

function StripeConnectSection() {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [linking, setLinking] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/driver/stripe/status");
      setStatus(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load payout status");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    // If returning from Stripe onboarding, re-fetch status
    const params = new URLSearchParams(window.location.search);
    if (params.has("stripe")) {
      window.history.replaceState({}, "", window.location.pathname);
      // Give Stripe a beat to propagate the account.updated webhook
      setTimeout(load, 500);
    }
  }, [load]);

  const startOnboarding = async () => {
    setLinking(true);
    setError("");
    try {
      const { data } = await apiClient.post("/driver/stripe/onboard-link");
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start Stripe onboarding");
      setLinking(false);
    }
  };

  const openStripeDashboard = async () => {
    setLinking(true);
    try {
      const { data } = await apiClient.post("/driver/stripe/login-link");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't open Stripe dashboard");
    } finally {
      setLinking(false);
    }
  };

  // While loading: minimal skeleton, no flash
  if (loading) {
    return (
      <section className="mb-12" data-testid="driver-stripe-section">
        <div className="bg-white border border-[#0A0A0A]/10 p-6 inline-flex items-center gap-2 text-sm text-[#475569]">
          <Loader2 size={14} className="animate-spin" /> Loading payout status…
        </div>
      </section>
    );
  }

  const fullyOnboarded = status?.connected && status?.charges_enabled && status?.payouts_enabled;
  const startedOnboarding = status?.connected && !fullyOnboarded;

  return (
    <section className="mb-12" data-testid="driver-stripe-section">
      <div
        className={`border p-6 ${
          fullyOnboarded
            ? "bg-[#5BB247]/5 border-[#5BB247]/40"
            : "bg-white border-[#0A0A0A]/10"
        }`}
      >
        <div className="flex items-start gap-4">
          <div className={`shrink-0 p-3 ${fullyOnboarded ? "bg-[#5BB247]/15" : "bg-[#1E4D55]/10"}`}>
            <Banknote size={24} className={fullyOnboarded ? "text-[#2c8642]" : "text-[#1E4D55]"} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-display text-xl font-bold tracking-tight text-[#0A0A0A]">
                {fullyOnboarded
                  ? "Bank account connected"
                  : startedOnboarding
                    ? "Finish your Stripe setup"
                    : "Connect your bank account"}
              </p>
              {fullyOnboarded && (
                <span data-testid="stripe-connected-badge" className="inline-flex items-center gap-1 bg-[#5BB247]/10 text-[#2c8642] border border-[#5BB247]/40 px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold">
                  <CheckCircle2 size={12} /> Active
                </span>
              )}
              {startedOnboarding && (
                <span data-testid="stripe-pending-badge" className="inline-flex items-center gap-1 bg-[#F5A623]/10 text-[#A66800] border border-[#F5A623]/40 px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-bold">
                  <Clock size={12} /> Action needed
                </span>
              )}
            </div>
            <p className="mt-2 text-[#475569] leading-relaxed max-w-2xl">
              {fullyOnboarded
                ? "You'll be paid into the bank account on file as soon as each escrow job completes. Manage your bank or tax details directly in your Stripe Express dashboard."
                : "Raanah holds customer payments in escrow until each job completes — then funds release to your bank automatically. Stripe handles bank verification securely (we never see your account number)."}
            </p>

            {error && (
              <div data-testid="stripe-error" className="mt-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-3">
              {!fullyOnboarded && (
                <button
                  type="button"
                  onClick={startOnboarding}
                  disabled={linking}
                  data-testid="stripe-onboard-button"
                  className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {linking ? (
                    <><Loader2 size={14} className="animate-spin" /><span>Opening Stripe…</span></>
                  ) : (
                    <>
                      <span>{startedOnboarding ? "Continue setup" : "Connect bank account"}</span>
                      <ArrowRight size={14} />
                    </>
                  )}
                </button>
              )}
              {fullyOnboarded && (
                <button
                  type="button"
                  onClick={openStripeDashboard}
                  disabled={linking}
                  data-testid="stripe-dashboard-button"
                  className="inline-flex items-center gap-2 bg-white border border-[#0A0A0A]/15 hover:border-[#1E4D55] text-[#0A0A0A] px-5 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {linking ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                  <span>Open Stripe dashboard</span>
                </button>
              )}
            </div>

            {startedOnboarding && status?.currently_due?.length > 0 && (
              <p className="mt-3 text-xs text-[#475569]">
                Stripe is still asking for: <strong>{status.currently_due.join(", ")}</strong>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Available Jobs ---------------- */

function AvailableJobsSection({ isVerified }) {
  const [jobs, setJobs] = React.useState([]);
  const [blocked, setBlocked] = React.useState("");
  const [eligible, setEligible] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [quoteJob, setQuoteJob] = React.useState(null);
  const [acceptingId, setAcceptingId] = React.useState(null);
  const [acceptError, setAcceptError] = React.useState("");
  const [refreshKey, setRefreshKey] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get("/driver/jobs/available");
      setJobs(data.jobs || []);
      setBlocked(data.blocked_reason || "");
      setEligible(data.eligible_categories || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load, refreshKey]);

  const acceptAutoPriced = async (job) => {
    if (!window.confirm(`Accept this £${(job.amount_pence/100).toFixed(2)} job? You'll be locked in as the driver.`)) return;
    setAcceptingId(job.id);
    setAcceptError("");
    try {
      await apiClient.post(`/driver/jobs/${job.id}/accept`);
      setRefreshKey((k) => k + 1);
      window.dispatchEvent(new CustomEvent("driver-quotes-changed"));
    } catch (e) {
      setAcceptError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't accept");
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <section className="mb-16" data-testid="driver-jobs-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">
          Available jobs
        </h2>
        {isVerified && (
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            data-testid="driver-jobs-refresh"
            className="text-xs uppercase tracking-[0.16em] font-bold text-[#1E4D55] hover:text-[#163C42]"
          >
            Refresh
          </button>
        )}
      </div>

      {!isVerified ? (
        <div data-testid="driver-jobs-locked" className="bg-white border border-[#F5A623]/40 bg-[#F5A623]/5 p-6 flex items-start gap-3">
          <ShieldAlert size={18} className="text-[#A66800] mt-0.5 shrink-0" />
          <p className="text-sm text-[#0A0A0A]">
            Once your required documents are approved, open customer jobs will appear here. You can browse the queue but quoting is locked until verification completes.
          </p>
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : loading ? (
        <div data-testid="driver-jobs-loading" className="text-[#475569] text-sm inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : blocked ? (
        <div data-testid="driver-jobs-blocked" className="bg-white border border-[#F5A623]/40 bg-[#F5A623]/5 p-6 text-sm text-[#0A0A0A]">{blocked}</div>
      ) : jobs.length === 0 ? (
        <div data-testid="driver-jobs-empty" className="bg-white border border-[#0A0A0A]/10 border-dashed p-10 text-center">
          <p className="font-display text-xl font-bold text-[#0A0A0A]">No open jobs right now.</p>
          <p className="mt-2 text-[#475569] max-w-md mx-auto">
            We'll surface new jobs in your area the moment customers post them. Eligible categories: <strong>{eligible.length}</strong>.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="driver-jobs-list">
          {jobs.map((j) => (
            <div
              key={j.id}
              data-testid={`driver-job-${j.id}`}
              className="bg-white border border-[#0A0A0A]/10 hover:border-[#1E4D55] transition-colors p-5"
            >
              <div className="flex flex-wrap items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#1E4D55]">
                      {j.category_label}
                    </span>
                    {j.auto_priced ? (
                      <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#2c8642] bg-[#5BB247]/10 border border-[#5BB247]/40 px-1.5 py-0.5">
                        Pre-paid · {gbp(j.amount_pence)}
                      </span>
                    ) : (
                      <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#475569]">
                        {j.quote_count} quote{j.quote_count === 1 ? "" : "s"} so far
                      </span>
                    )}
                  </div>
                  <p className="mt-2 font-display text-xl font-bold tracking-tight text-[#0A0A0A]">
                    {j.title}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#475569]">
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={12} /> {j.pickup_address}{j.dropoff_address ? ` → ${j.dropoff_address}` : ""}
                    </span>
                    {j.scheduled_date && (
                      <span className="inline-flex items-center gap-1"><Calendar size={12} /> {j.scheduled_date}</span>
                    )}
                    {j.auto_priced && j.distance_mi != null && (
                      <span className="inline-flex items-center gap-1">{j.distance_mi} mi · {Math.round(j.drive_time_min)}min drive · {j.estimated_hours}h booked</span>
                    )}
                    {!j.auto_priced && j.budget_pence != null && (
                      <span className="inline-flex items-center gap-1"><Tag size={12} /> Customer hint: {gbp(j.budget_pence)}</span>
                    )}
                  </div>
                  {j.description && (
                    <p className="mt-3 text-sm text-[#475569] line-clamp-2 whitespace-pre-wrap">{j.description}</p>
                  )}
                </div>
                <div className="shrink-0">
                  {j.auto_priced ? (
                    <button
                      onClick={() => acceptAutoPriced(j)}
                      disabled={acceptingId === j.id}
                      data-testid={`driver-accept-button-${j.id}`}
                      className="inline-flex items-center gap-2 bg-[#5BB247] hover:bg-[#4A9939] text-white transition-colors px-5 py-3 text-sm font-semibold disabled:opacity-60"
                    >
                      {acceptingId === j.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      <span>Accept {gbp(j.amount_pence)}</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setQuoteJob(j)}
                      data-testid={`driver-quote-button-${j.id}`}
                      className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white transition-colors px-5 py-3 text-sm font-semibold"
                    >
                      <Send size={14} />
                      <span>Submit quote</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {acceptError && (
        <div data-testid="driver-accept-error" className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{acceptError}</span>
        </div>
      )}

      {quoteJob && (
        <SubmitQuoteModal
          job={quoteJob}
          onClose={() => setQuoteJob(null)}
          onSubmitted={() => {
            setQuoteJob(null);
            setRefreshKey((k) => k + 1);
            // Also trigger a quotes section refresh via event
            window.dispatchEvent(new CustomEvent("driver-quotes-changed"));
          }}
        />
      )}
    </section>
  );
}

/* ---------------- Submit Quote Modal ---------------- */

function SubmitQuoteModal({ job, onClose, onSubmitted }) {
  const [amount, setAmount] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const pence = Math.round(parseFloat(amount) * 100);
    if (!pence || Number.isNaN(pence) || pence < 2000) {
      setError("Quote must be at least £20.");
      return;
    }
    if (pence > 5_000_000) {
      setError("Quote can't exceed £50,000.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/driver/jobs/${job.id}/quote`, {
        amount_pence: pence,
        message: message.trim() || null,
      });
      onSubmitted();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit quote");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="submit-quote-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-lg w-full p-8 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">
              {job.category_label}
            </p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">
              Quote: {job.title}
            </h3>
          </div>
          <button onClick={onClose} data-testid="submit-quote-close" className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
        </div>

        <div className="mt-4 text-xs text-[#475569] flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {job.pickup_address}{job.dropoff_address ? ` → ${job.dropoff_address}` : ""}</span>
          {job.scheduled_date && (
            <span className="inline-flex items-center gap-1"><Calendar size={12} /> {job.scheduled_date}</span>
          )}
          {job.budget_pence != null && (
            <span className="inline-flex items-center gap-1"><Tag size={12} /> Customer hint: {gbp(job.budget_pence)}</span>
          )}
        </div>

        {job.description && (
          <p className="mt-4 text-sm text-[#0A0A0A] whitespace-pre-wrap bg-[#F5F5F5] border border-[#0A0A0A]/5 p-3">{job.description}</p>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="submit-quote-form">
          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Your quote (£)</label>
            <input
              type="number" required min="20" step="1" max="50000"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 285"
              data-testid="submit-quote-amount"
              className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
            />
            <p className="mt-1.5 text-[11px] text-[#475569]">Min £20 · Max £50,000. Held in escrow until job completion.</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-1.5">Message (optional)</label>
            <textarea
              maxLength={1000} rows={3}
              value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Anything the customer should know? e.g. arrival window, van size, helpers, etc."
              data-testid="submit-quote-message"
              className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
            />
          </div>

          {error && (
            <div data-testid="submit-quote-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} data-testid="submit-quote-cancel" className="px-5 py-3 text-sm font-semibold text-[#475569] hover:text-[#0A0A0A]">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="submit-quote-submit" className="inline-flex items-center gap-2 bg-[#1E4D55] text-white hover:bg-[#163C42] transition-colors px-5 py-3 font-semibold disabled:opacity-60">
              {submitting ? (<><Loader2 size={16} className="animate-spin" /><span>Submitting…</span></>) : (<><Send size={16} /><span>Send quote</span></>)}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

/* ---------------- My Quotes ---------------- */

function MyQuotesSection() {
  const [quotes, setQuotes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [acting, setActing] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get("/driver/quotes");
      setQuotes(data.quotes || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load your quotes");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("driver-quotes-changed", handler);
    return () => window.removeEventListener("driver-quotes-changed", handler);
  }, [load]);

  const withdraw = async (id) => {
    if (!window.confirm("Withdraw this quote?")) return;
    setActing(id);
    try {
      await apiClient.delete(`/driver/quotes/${id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to withdraw");
    } finally {
      setActing(null);
    }
  };

  return (
    <section className="mb-16" data-testid="driver-my-quotes-section">
      <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] mb-6">
        Your quotes
      </h2>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div data-testid="driver-my-quotes-loading" className="text-[#475569] text-sm inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : quotes.length === 0 ? (
        <div data-testid="driver-my-quotes-empty" className="bg-white border border-[#0A0A0A]/10 border-dashed p-10 text-center">
          <FileText size={20} className="mx-auto text-[#475569]" />
          <p className="mt-3 font-display text-lg font-bold text-[#0A0A0A]">No quotes yet.</p>
          <p className="mt-1 text-sm text-[#475569]">Submit your first quote on a job above.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="driver-my-quotes-list">
          {quotes.map((q) => (
            <div
              key={q.id}
              data-testid={`driver-my-quote-${q.id}`}
              className={`border p-4 flex flex-wrap items-start gap-4 ${
                q.status === "accepted"
                  ? "border-[#5BB247]/60 bg-[#5BB247]/5"
                  : "border-[#0A0A0A]/10 bg-white"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#1E4D55]">
                    {q.job?.category_label || "Job"}
                  </span>
                  <QuoteStatusPill status={q.status} />
                </div>
                <p className="mt-2 font-display text-lg font-bold tracking-tight text-[#0A0A0A]">
                  {q.job?.title || "Job removed"}
                </p>
                {q.message && (
                  <p className="mt-1 text-sm text-[#475569]">{q.message}</p>
                )}
                {q.job?.scheduled_date && (
                  <p className="mt-1 text-xs text-[#475569] inline-flex items-center gap-1">
                    <Calendar size={12} /> {q.job.scheduled_date}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="font-display text-2xl font-black tracking-tighter text-[#1E4D55]">
                  {gbp(q.amount_pence)}
                </p>
                {q.status === "pending" && (
                  <button
                    onClick={() => withdraw(q.id)}
                    disabled={acting === q.id}
                    data-testid={`driver-withdraw-quote-${q.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#B91C1C] hover:underline disabled:opacity-60"
                  >
                    {acting === q.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Withdraw
                  </button>
                )}
                {q.status === "accepted" && (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#2c8642]">
                    <CheckCircle2 size={12} /> Customer accepted
                  </p>
                )}
                {q.status === "pending" && (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs text-[#475569]">
                    <Clock size={12} /> Awaiting customer
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
